import { useRef, useEffect, useState, useMemo } from 'react';
import { useLocation, NavLink, Outlet } from 'react-router-dom';
import queryString from 'query-string';
import {
  IconArrowLeft,
  Table,
  useQueryState,
  Filters,
  useFilters,
  useGetList,
  GeneralLedgerTransaction,
  SchemaFilterType,
  SortDir,
  IconLoader,
  SelectItemProps,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  clearRestatementTrasnactionsListState,
  setSelectedTake,
} from './store/slice';
import {
  columns,
  columnsLabels,
  filtersSchema,
  schema,
} from './variables/list';
import { useLazyGetRestatementTransactionsListQuery } from './store/service';
import useGetAccountsAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import formatGLList from '../general-ledger/helpers/format-gl-list';

import cn from './styles.module.css';

const RestatementTransactions = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [takeCount, setTakeCount] = useState(0);
  const [backlinkPath, setBacklinkPath] = useState('');
  const [ids, setIds] = useState<string[]>([]);

  const { search } = useLocation();

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(schema);

  const {
    restatementTransactions: {
      list: { transactions, pagination },
    },
    transactionsDictionaries: { currencies },
  } = useAppSelector((state) => state);

  const {
    autocompleteList: debitAutocompleteList,
    onSearch: onSearchDebit,
    onClear,
  } = useGetAccountsAutocompleteList('', 'id');
  const {
    autocompleteList: creditAutocompleteList,
    onSearch: onSearchCredit,
    onClear: onCreditClear,
  } = useGetAccountsAutocompleteList('', 'id');

  const [getTransactionsList, { isLoading, isFetching }] =
    useLazyGetRestatementTransactionsListQuery();

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<GeneralLedgerTransaction>({
      getList: getTransactionsList,
      onFilterChange,
      onSortChange,
      schema,
    });

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name.toLowerCase().indexOf('debit') >= 0) {
      onSearchDebit(searchAutocomplete);
    } else if (name.toLowerCase().indexOf('credit') >= 0) {
      onSearchCredit(searchAutocomplete);
    }
  };

  const onClearAutoComplete = () => {
    onClear();

    setTimeout(() => {
      onCreditClear();
    }, 100);
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 45;

        setTakeCount(Math.ceil(take + 3));
        dispatch(setSelectedTake(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (search && takeCount !== 0) {
      const parsed = queryString.parse(search);

      if (parsed) {
        if (parsed?.backLink) {
          setBacklinkPath(parsed?.backLink as string);
        }

        if (parsed?.ids || parsed?.id) {
          let idsArray: string[] = [];

          if (parsed.ids) {
            idsArray = (parsed?.ids as string)?.split(',');
          } else if (parsed.id) {
            idsArray = parsed.id as string[];
          }

          setIds(idsArray);

          getListAction({
            filters: {
              ...filters,
              take: {
                value: [takeCount.toString()],
                type: SchemaFilterType.STRING,
              },
              id: {
                value: idsArray,
                type: SchemaFilterType.STRING,
              },
            },
            sort,
          });
        }
      }
    }
  }, [search, takeCount]);

  useEffect(() => {
    setValues('debitAccountId', debitAutocompleteList);
    setValues('creditAccountId', creditAutocompleteList);
    setValues('currencyId', currencies as SelectItemProps[]);
  }, [debitAutocompleteList, creditAutocompleteList, currencies]);

  useEffect(
    () => () => {
      dispatch(clearRestatementTrasnactionsListState());
    },
    [dispatch],
  );

  const formattedData = useMemo(() => {
    const data = formatGLList({
      list: transactions as GeneralLedgerTransaction[],
    });

    return data;
  }, [transactions]);

  let backLink = '/transactions/general-ledger';

  if (backlinkPath === 'transaction-types') {
    backLink = '/administration/settings/transaction-types';
  } else if (backlinkPath === 'balance') {
    backLink = '/reports/balance/sheet';
  } else if (backlinkPath === 'bank-and-psp') {
    backLink = '/reports/bank-and-psp';
  } else if (backlinkPath === 'capital-adequacy') {
    backLink = '/reports/capital-adequacy';
  } else if (backlinkPath === 'currency-position') {
    backLink = '/reports/currency-position';
  } else if (backlinkPath === 'profit-loss') {
    backLink = '/reports/profit-loss/sheet';
  } else if (backlinkPath === 'revaluation') {
    backLink = '/reports/revaluation';
  } else if (backlinkPath === 'safeguarding') {
    backLink = '/reports/safeguarding';
  }

  let content;

  if (isLoading || isFetching) {
    content = (
      <div className={cn.restatementsTransactionsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <Table
        title="Removed and added transactions"
        columns={columns}
        columnsLabels={columnsLabels}
        data={formattedData || []}
        filters={filters}
        sort={sort}
        filtersComponent={filtersComponent}
        onSort={(name) => {
          onSort(name);
        }}
        onFilterChange={(values) => {
          const nextFilters: Filters = {
            ...values,
            sortProp: {
              value: sort.sortProp ? [sort.sortProp] : ['transferDate'],
              type: SchemaFilterType.STRING,
            },
            sortDir: {
              value: sort.sortDir ? [sort.sortDir] : [SortDir.DESC],
              type: SchemaFilterType.STRING,
            },
            take: {
              value: [takeCount.toString()],
              type: SchemaFilterType.STRING,
            },
            id: {
              value: ids,
              type: SchemaFilterType.STRING,
            },
          };

          onFilter({
            ...nextFilters,
          });
        }}
        handleSubmit={handleSubmit}
        isLoading={
          isLoading ||
          isFetching ||
          takeCount === 0 ||
          transactions === undefined
        }
        take={takeCount.toString()}
        setPage={(data) => {
          setPage({
            ...data,
            filters: {
              ...filters,
              take: {
                value: [takeCount.toString()],
                type: SchemaFilterType.STRING,
              },
            },
          });
        }}
        onClearAutoComplete={onClearAutoComplete}
        hiddenFiltersTags={['id']}
        pagination={pagination}
        storeKey="restatement-transactions"
        paginationDisabled={isFetching}
        error={errorList}
        isFetching={isFetching}
        transactionTable
        withVerticalScroll
        withSettings
        withScroll
        clickable
      />
    );
  }

  return (
    <div className={cn.restatementTransactions}>
      <div className={cn.restatementTransactionsWrapper} ref={scrollRef}>
        <div className={cn.backLinkWrapper}>
          <NavLink to={backLink} className={cn.backLink}>
            <IconArrowLeft /> Back
          </NavLink>
        </div>
        {content}
        <Outlet />
      </div>
    </div>
  );
};

export default RestatementTransactions;
